import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, History, Loader2, TrendingUp, TrendingDown, 
  Calendar, Download, RefreshCw, Brain, Target, 
  CheckCircle2, XCircle, BarChart3, ArrowUpRight, ArrowDownRight, SlidersHorizontal, Play,
  MinusCircle, DollarSign
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strategy badge colors (BUY/SHORT only)
const STRATEGY_COLORS = {
  BUY: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  SHORT: "bg-red-500/20 text-red-400 border-red-500/30",
};

// Recommendation badge colors
const RECOMMENDATION_COLORS = {
  TRADE: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  AVOID: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
};

// Available columns for the history table
const AVAILABLE_COLUMNS = [
  { id: 'date', label: 'Trade Date', default: false },
  { id: 'eval_datetime', label: 'Evaluation Time', default: false },
  { id: 'symbol', label: 'Stock Name', default: true },
  { id: 'sector', label: 'Industry', default: false },
  { id: 'rank_score', label: 'Rank Score', default: false },
  { id: 'strategy', label: 'Trading Signal', default: true },
  { id: 'recommendation', label: 'Recommendation', default: false },
  { id: 'shares', label: 'Number of Shares', default: true },
  { id: 'investment', label: 'Investment Amount', default: true },
  { id: 'start_price', label: 'Entry Price', default: true },
  { id: 'predicted_price', label: 'Target Price (2-Day)', default: true },
  { id: 'actual_price', label: 'Actual Price', default: true },
  { id: 'gross_pl', label: 'Gross Profit / Loss', default: false },
  { id: 'commission', label: 'Brokerage Fee', default: false },
  { id: 'net_pl', label: 'Net Profit / Loss', default: true },
  { id: 'eval_date', label: 'Review Date', default: true },
  { id: 'result', label: 'Trade Outcome', default: true },
  { id: 'success', label: 'Prediction Success', default: true },
  { id: 'actions', label: 'Actions', default: false },
];

export default function AITradeHistory() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tradePlans, setTradePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState(null);  // New: history statistics
  const [learningMetrics, setLearningMetrics] = useState(null);
  const [days, setDays] = useState(30);
  
  // Available dates and selection
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState([]);
  
  // Investment calculator
  const [investmentAmount, setInvestmentAmount] = useState(() => {
    const saved = localStorage.getItem('history_investment_amount');
    return saved ? Number(saved) : 1000000;
  });
  const [commissionPct, setCommissionPct] = useState(() => {
    const saved = localStorage.getItem('history_commission_pct');
    return saved ? Number(saved) : 0.2;
  });
  
  // Save to localStorage when values change
  useEffect(() => {
    localStorage.setItem('history_investment_amount', String(investmentAmount));
  }, [investmentAmount]);
  
  useEffect(() => {
    localStorage.setItem('history_commission_pct', String(commissionPct));
  }, [commissionPct]);
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(
    AVAILABLE_COLUMNS.filter(c => c.default).map(c => c.id)
  );

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const isColumnVisible = (columnId) => visibleColumns.includes(columnId);
  
  // Select All / Deselect All columns
  const selectAllColumns = () => {
    setVisibleColumns(AVAILABLE_COLUMNS.map(c => c.id));
  };
  
  const deselectAllColumns = () => {
    // Keep at least symbol visible
    setVisibleColumns(['symbol']);
  };

  // Force evaluation state
  const [evaluating, setEvaluating] = useState(false);
  const [showForceEvalDialog, setShowForceEvalDialog] = useState(false);
  const [forceEvalDate, setForceEvalDate] = useState('');
  
  // Re-evaluating individual stock
  const [reEvaluatingStock, setReEvaluatingStock] = useState(null);
  
  // Re-evaluate dialog state
  const [showReEvalDialog, setShowReEvalDialog] = useState(false);
  const [reEvalSymbol, setReEvalSymbol] = useState(null);
  const [reEvalDate, setReEvalDate] = useState('');
  const [reEvalTradeEvalDate, setReEvalTradeEvalDate] = useState('');
  
  // Open re-evaluate dialog
  const openReEvalDialog = (trade) => {
    setReEvalSymbol(trade.symbol);
    setReEvalDate(''); // Reset date input
    setReEvalTradeEvalDate(trade.evaluation_date || ''); // Store the trade's evaluation date for reference
    setShowReEvalDialog(true);
  };
  
  // Re-evaluate single stock with optional date
  const reEvaluateStock = async () => {
    if (!reEvalSymbol) return;
    
    try {
      setReEvaluatingStock(reEvalSymbol);
      setShowReEvalDialog(false);
      const cleanSymbol = reEvalSymbol.replace('.NS', '').replace('.BO', '');
      
      // Build URL with optional date parameter
      let url = `${API}/ai/re-evaluate-stock/${cleanSymbol}`;
      if (reEvalDate) {
        url += `?eval_date=${reEvalDate}`;
      }
      
      const res = await axios.post(url);
      toast.success(`${cleanSymbol}: ${res.data.evaluation_result} at ₹${res.data.actual_price} (${res.data.evaluation_date})`);
      // Refresh the trades list
      fetchTradePlans();
    } catch (err) {
      console.error("Error re-evaluating stock:", err);
      toast.error(err.response?.data?.detail || `Failed to re-evaluate ${reEvalSymbol}`);
    } finally {
      setReEvaluatingStock(null);
      setReEvalSymbol(null);
      setReEvalDate('');
    }
  };

  // Fetch available dates
  const fetchAvailableDates = useCallback(async () => {
    try {
      // Fetch all dates from all date fields
      const res = await axios.get(`${API}/ai/trade-history/dates?field=trade_date`);
      setAvailableDates(res.data.dates || []);
    } catch (err) {
      console.error("Error fetching available dates:", err);
    }
  }, []);

  // Fetch all trade plans
  const fetchTradePlans = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch trade history with date filtering
      const cacheBuster = Date.now();
      let url = `${API}/ai/trade-history`;
      if (selectedDates.length > 0) {
        url += `?dates=${selectedDates.join(',')}&field=trade_date&_t=${cacheBuster}`;  // Changed from 'all' to 'trade_date'
      } else {
        url += `?days=${days}&_t=${cacheBuster}`;
      }
      
      const historyRes = await axios.get(url, {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      setTradeHistory(historyRes.data.trades || []);
      setHistoryStats({
        total: historyRes.data.total || 0,
        successful: historyRes.data.successful || 0,
        not_successful: historyRes.data.not_successful || 0,
        successful_pct: historyRes.data.successful_pct || 0,
        not_successful_pct: historyRes.data.not_successful_pct || 0,
        total_pl: historyRes.data.total_pl || 0,
      });
      
      // Fetch latest trade plan
      try {
        const planRes = await axios.get(`${API}/ai/trade-plan/latest`);
        setSelectedPlan(planRes.data);
      } catch (e) {
        // No trade plan yet
        setSelectedPlan(null);
      }
      
      // Fetch learning metrics
      try {
        const metricsRes = await axios.get(`${API}/ai/learning-metrics`);
        setLearningMetrics(metricsRes.data);
      } catch (e) {
        setLearningMetrics(null);
      }
      
    } catch (err) {
      console.error("Error fetching trade history:", err);
      toast.error("Failed to fetch trade history");
    } finally {
      setLoading(false);
    }
  }, [days, selectedDates]);

  // Open Force Evaluation dialog
  const openForceEvalDialog = () => {
    setForceEvalDate(''); // Reset date
    setShowForceEvalDialog(true);
  };
  
  // Force evaluation - re-evaluate ALL stocks using historical closing prices
  const runForceEvaluation = async () => {
    try {
      setShowForceEvalDialog(false);
      setEvaluating(true);
      
      const dateMsg = forceEvalDate 
        ? `Using override date: ${forceEvalDate}` 
        : "Using each stock's review date";
      
      const stockMsg = selectedDates.length > 0
        ? `Re-evaluating ${selectedDates.length} selected date(s)... ${dateMsg}`
        : `Re-evaluating all stocks... ${dateMsg}`;
      
      toast.info(stockMsg, { duration: 60000 });
      
      // Build request with filter dates if any are selected
      const requestData = {
        eval_date: forceEvalDate || null
      };
      
      // If specific dates are selected, only evaluate those
      if (selectedDates.length > 0) {
        requestData.filter_dates = selectedDates.join(',');
      }
      
      const res = await axios.post(`${API}/ai/force-evaluate-all`, requestData);
      
      const results = res.data.results || {};
      const message = selectedDates.length > 0 
        ? `Evaluated ${results.total || 0} stocks from ${selectedDates.length} date(s): ${results.wins || 0} WIN, ${results.losses || 0} LOSS (${results.success_rate || 0}% success rate)`
        : `Evaluation complete! ${results.wins || 0} WIN, ${results.losses || 0} LOSS (${results.success_rate || 0}% success rate)`;
      
      toast.success(message);
      
      // Refresh data
      fetchTradePlans();
    } catch (err) {
      console.error("Error running evaluation:", err);
      const errorMsg = err.response?.data?.detail 
        || err.response?.data?.message
        || (typeof err.message === 'string' ? err.message : 'Unknown error')
        || JSON.stringify(err);
      toast.error(`Evaluation failed: ${errorMsg}`);
    } finally {
      setEvaluating(false);
      setForceEvalDate('');
    }
  };

  useEffect(() => {
    fetchAvailableDates();
  }, [fetchAvailableDates]);

  // Auto-select all dates when available dates are loaded (on initial mount)
  useEffect(() => {
    if (availableDates.length > 0 && selectedDates.length === 0) {
      setSelectedDates(availableDates);
    }
  }, [availableDates]);

  useEffect(() => {
    fetchTradePlans();
  }, [fetchTradePlans]);

  // Group dates by month
  const datesByMonth = useMemo(() => {
    const grouped = {};
    
    availableDates.forEach(date => {
      try {
        // Validate date format (YYYY-MM-DD)
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return; // Skip invalid format
        }
        
        const dateObj = new Date(date + 'T00:00:00');
        
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
          return; // Skip invalid dates
        }
        
        const monthKey = dateObj.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
        
        if (!grouped[monthKey]) {
          grouped[monthKey] = {
            key: monthKey,
            dates: [],
            displayName: monthKey
          };
        }
        
        grouped[monthKey].dates.push(date);
      } catch (e) {
        // Skip any dates that cause errors
        return;
      }
    });
    
    // Sort months in descending order (newest first)
    return Object.values(grouped).sort((a, b) => {
      const dateA = new Date(a.dates[0] + 'T00:00:00');
      const dateB = new Date(b.dates[0] + 'T00:00:00');
      return dateB - dateA;
    });
  }, [availableDates]);

  // Toggle month expansion
  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev =>
      prev.includes(monthKey)
        ? prev.filter(m => m !== monthKey)
        : [...prev, monthKey]
    );
  };

  // Toggle date selection
  const toggleDate = (date) => {
    setSelectedDates(prev =>
      prev.includes(date)
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  };

  // Toggle all dates in a month
  const toggleMonthDates = (monthDates) => {
    const allSelected = monthDates.every(date => selectedDates.includes(date));
    
    if (allSelected) {
      // Deselect all dates in this month
      setSelectedDates(prev => prev.filter(d => !monthDates.includes(d)));
    } else {
      // Select all dates in this month
      setSelectedDates(prev => [...new Set([...prev, ...monthDates])]);
    }
  };

  // Select all dates
  const selectAllDates = () => {
    setSelectedDates([...availableDates]);
  };

  // Deselect all dates
  const deselectAllDates = () => {
    setSelectedDates([]);
  };

  // Format currency
  const formatCurrency = (value, decimals = 2) => {
    if (value === null || value === undefined) return "—";
    return `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: decimals })}`;
  };

  // Format percentage
  const formatPct = (value) => {
    if (value === null || value === undefined) return "—";
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
  };

  // Group trades by date
  const groupedTrades = tradeHistory.reduce((acc, trade) => {
    const date = trade.date || trade.trade_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(trade);
    return acc;
  }, {});

  // Calculate trade with investment allocation
  const calculateTradeWithAllocation = useCallback((trade, allocationPerStock) => {
    const startPrice = trade.start_price || 0;
    const predictedPrice = trade.predicted_price;  // Keep null as null
    const actualPrice = trade.actual_price || 0;
    
    // Use strategy from database - don't recalculate
    const strategy = trade.strategy || 'BUY';
    
    // Calculate shares based on allocation
    const shares = startPrice > 0 ? Math.floor(allocationPerStock / startPrice) : 0;
    const investment = shares * startPrice;
    
    // Calculate P/L only if actual price is available
    let grossProfit = 0;
    let commission = 0;
    let netProfit = 0;
    
    if (actualPrice > 0 && shares > 0) {
      // For BUY: profit = (actual - start) * shares
      // For SHORT: profit = (start - actual) * shares
      grossProfit = strategy === 'BUY' 
        ? (actualPrice - startPrice) * shares
        : (startPrice - actualPrice) * shares;
      
      commission = investment * (commissionPct / 100);
      netProfit = grossProfit - commission;
    }
    
    return {
      ...trade,
      calculated_strategy: strategy,
      calculated_shares: shares,
      calculated_investment: investment,
      calculated_gross_profit: grossProfit,
      calculated_commission: commission,
      calculated_net_profit: netProfit,
    };
  }, [commissionPct]);

  // Calculate trades with allocation
  const tradesWithAllocation = useMemo(() => {
    if (tradeHistory.length === 0) return [];
    
    const allocationPerStock = investmentAmount / tradeHistory.length;
    return tradeHistory.map(trade => calculateTradeWithAllocation(trade, allocationPerStock));
  }, [tradeHistory, investmentAmount, calculateTradeWithAllocation]);

  // Calculate totals using the allocated trades
  const totals = useMemo(() => {
    return tradesWithAllocation.reduce((acc, trade) => {
      const netProfit = trade.calculated_net_profit || 0;
      const actualPrice = trade.actual_price || 0;
      const startPrice = trade.start_price || 0;
      const strategy = trade.calculated_strategy;
      
      acc.totalInvested += trade.calculated_investment || 0;
      acc.totalGrossProfit += trade.calculated_gross_profit || 0;
      acc.totalCommission += trade.calculated_commission || 0;
      acc.totalNetProfit += trade.calculated_net_profit || 0;
      
      // Use evaluation_result field (WIN/LOSS) from database
      const result = trade.evaluation_result || trade.result;
      if (result === 'WIN') {
        acc.successful += 1;
      } else if (result === 'LOSS') {
        acc.failed += 1;
      }
      
      acc.total += 1;
      return acc;
    }, { 
      totalGrossProfit: 0, 
      totalCommission: 0, 
      totalNetProfit: 0, 
      totalInvested: 0, 
      successful: 0,
      failed: 0, 
      total: 0 
    });
  }, [tradesWithAllocation]);

  const successRate = totals.total > 0 ? (totals.successful / totals.total * 100) : 0;
  const failedRate = totals.total > 0 ? (totals.failed / totals.total * 100) : 0;

  // Download CSV
  const downloadCSV = () => {
    if (tradesWithAllocation.length === 0) {
      toast.error("No data to download");
      return;
    }

    const headers = ["Trade Date", "Review Date", "Evaluation Time", "Stock Name", "Industry", "Trading Signal", "Number of Shares", "Investment Amount", "Entry Price", "Target Price (2-Day)", "Actual Price", "Gross Profit / Loss", "Brokerage Fee", "Net Profit / Loss", "Trade Outcome", "Prediction Success"];
    const rows = tradesWithAllocation.map(t => {
      const resultStatus = t.actual_price 
        ? (t.calculated_net_profit > 0 ? 'Successful' : 'Not Successful')
        : 'Pending';
      
      return [
        t.date || t.trade_date,
        t.evaluation_date || '',
        t.evaluated_at ? new Date(t.evaluated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '',
        t.symbol?.replace('.NS', '').replace('.BO', ''),
        t.sector || '',
        t.calculated_strategy,
        t.calculated_shares,
        t.calculated_investment?.toFixed(2),
        (t.start_price || 0).toFixed(2),
        t.predicted_price ? t.predicted_price.toFixed(2) : 'N/A',  // Show N/A for null
        t.actual_price?.toFixed(2) || '',
        t.calculated_gross_profit?.toFixed(2) || '',
        t.calculated_commission?.toFixed(2) || '',
        t.calculated_net_profit?.toFixed(2) || '',
        resultStatus,
        t.calculated_net_profit > 0 ? "Yes" : "No"
      ];
    });

    const csvContent = [
      `# AI Trade History - Last ${days} Days`,
      `# Downloaded: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`,
      `# Total Investment: ${investmentAmount}`,
      `# Commission: ${commissionPct}%`,
      '',
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ai_trade_history_${days}days_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Downloaded trade history CSV");
  };

  return (
    <div data-testid="ai-history-page" className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="mb-2 gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Brain className="h-8 w-8 text-[hsl(var(--primary))]" />
              AI Trade History
            </h1>
            <p className="text-muted-foreground mt-1">
              View evaluated trades from the 13-module AI system
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Selector */}
            <Popover open={showDateSelector} onOpenChange={setShowDateSelector}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-[180px] justify-start gap-2">
                  <Calendar className="h-4 w-4" />
                  {selectedDates.length === 0 
                    ? `Last ${days} days`
                    : selectedDates.length === availableDates.length
                      ? 'All Dates'
                      : `${selectedDates.length} date${selectedDates.length > 1 ? 's' : ''}`
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-h-[500px] overflow-hidden" align="start">
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-[hsl(var(--border))] pb-3 sticky top-0 bg-[hsl(var(--popover))] z-10">
                    <h4 className="font-medium text-sm">Select Trade Dates</h4>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 px-3 text-xs"
                        onClick={selectAllDates}
                      >
                        All
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 px-3 text-xs"
                        onClick={deselectAllDates}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-[420px] overflow-y-auto pr-2 space-y-2">
                    {availableDates.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No dates available
                      </p>
                    ) : (
                      datesByMonth.map(month => {
                        const isExpanded = expandedMonths.includes(month.key);
                        const monthDatesSelected = month.dates.filter(d => selectedDates.includes(d)).length;
                        const allMonthSelected = monthDatesSelected === month.dates.length;
                        const someMonthSelected = monthDatesSelected > 0 && monthDatesSelected < month.dates.length;
                        
                        return (
                          <div key={month.key} className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--card))]">
                            {/* Month Header */}
                            <div 
                              className="flex items-center justify-between p-3 hover:bg-[hsl(var(--accent))] cursor-pointer transition-colors"
                              onClick={() => toggleMonth(month.key)}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <div onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={allMonthSelected}
                                    data-state={someMonthSelected ? 'indeterminate' : allMonthSelected ? 'checked' : 'unchecked'}
                                    onCheckedChange={() => toggleMonthDates(month.dates)}
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-semibold">
                                    {month.displayName}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {monthDatesSelected > 0 ? `${monthDatesSelected}/` : ''}{month.dates.length} {month.dates.length === 1 ? 'date' : 'dates'}
                                  </div>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {isExpanded ? '▼' : '▶'}
                                </div>
                              </div>
                            </div>
                            
                            {/* Month Dates (Collapsible) */}
                            {isExpanded && (
                              <div className="border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-2 space-y-1.5 max-h-[200px] overflow-y-auto">
                                {month.dates.map(date => {
                                  try {
                                    const dateObj = new Date(date + 'T00:00:00');
                                    if (isNaN(dateObj.getTime())) return null;
                                    
                                    return (
                                      <div key={date} className="flex items-center space-x-2 pl-8 py-1 hover:bg-[hsl(var(--accent))]/50 rounded transition-colors">
                                        <Checkbox 
                                          id={`date-${date}`}
                                          checked={selectedDates.includes(date)}
                                          onCheckedChange={() => toggleDate(date)}
                                        />
                                        <label 
                                          htmlFor={`date-${date}`}
                                          className="text-xs cursor-pointer font-mono flex-1"
                                        >
                                          {dateObj.toLocaleDateString('en-IN', { 
                                            day: '2-digit', 
                                            month: 'short',
                                            weekday: 'short'
                                          })}
                                        </label>
                                      </div>
                                    );
                                  } catch (e) {
                                    return null; // Skip invalid dates
                                  }
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Button variant="outline" size="sm" onClick={fetchTradePlans} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            
            {/* Force Evaluation Button - Admin only */}
            {user?.role !== 'guest' && (
              <Button 
                variant="default" 
                size="sm" 
                onClick={openForceEvalDialog} 
                disabled={evaluating}
                className="gap-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
                data-testid="force-evaluation-btn"
              >
                {evaluating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Force Evaluation
              </Button>
            )}
            
            {/* Column Selector */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  Columns
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="end">
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-[hsl(var(--border))] pb-3">
                    <h4 className="font-medium text-sm">Toggle Columns</h4>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 px-3 text-xs"
                        onClick={selectAllColumns}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 px-3 text-xs"
                        onClick={deselectAllColumns}
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {AVAILABLE_COLUMNS.map(col => (
                      <div key={col.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`hist-col-${col.id}`}
                          checked={isColumnVisible(col.id)}
                          onCheckedChange={() => toggleColumn(col.id)}
                        />
                        <label 
                          htmlFor={`hist-col-${col.id}`}
                          className="text-sm cursor-pointer"
                        >
                          {col.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Button variant="outline" size="sm" onClick={downloadCSV} disabled={tradeHistory.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Total Trades</div>
            <div className="text-2xl font-bold">{totals.total}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Success</div>
            <div className="text-2xl font-bold text-emerald-400">
              {totals.successful}
            </div>
            <div className="text-xs text-muted-foreground">
              {successRate.toFixed(1)}% profitable
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Not Successful</div>
            <div className="text-2xl font-bold text-red-400">
              {totals.failed}
            </div>
            <div className="text-xs text-muted-foreground">
              {failedRate.toFixed(1)}% not profitable
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Total Invested</div>
            <div className="text-2xl font-bold font-mono">{formatCurrency(totals.totalInvested, 0)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Net P/L</div>
            <div className={`text-2xl font-bold font-mono ${totals.totalNetProfit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {formatCurrency(totals.totalNetProfit, 0)}
            </div>
          </Card>
        </div>

        {/* Investment Calculator */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <DollarSign className="h-5 w-5 text-[hsl(var(--primary))]" />
            Investment Calculator
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Input Fields */}
            <div>
              <Label htmlFor="history-investment">Total Investment (₹)</Label>
              <Input
                id="history-investment"
                type="number"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="history-commission">Commission (%)</Label>
              <Input
                id="history-commission"
                type="number"
                step="0.1"
                value={commissionPct}
                onChange={(e) => setCommissionPct(Number(e.target.value))}
                className="mt-1 font-mono"
              />
            </div>
            {/* Calculated Values */}
            <div className="bg-[hsl(var(--surface))] rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Allocation per Trade</div>
              <div className="text-lg font-bold font-mono">
                {formatCurrency(totals.total > 0 ? investmentAmount / totals.total : 0, 0)}
              </div>
            </div>
            <div className="bg-[hsl(var(--surface))] rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Total Commission</div>
              <div className="text-lg font-bold font-mono text-red-400">
                -{formatCurrency(totals.totalCommission, 0)}
              </div>
            </div>
          </div>
        </Card>

        {/* AI Learning Metrics - Only show if there's actual data */}
        {learningMetrics && learningMetrics.status !== "no_data" && learningMetrics.trades_analyzed > 0 && (
          <Card className="p-4">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5 text-[hsl(var(--primary))]" />
              AI Learning Metrics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Prediction Accuracy</div>
                <div className="text-xl font-bold">{learningMetrics.overall_metrics?.success_rate?.toFixed(1) || 0}%</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Avg Profit/Trade</div>
                <div className="text-xl font-bold font-mono">{formatCurrency(learningMetrics.overall_metrics?.avg_profit || 0)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Strategy Bias</div>
                <div className="text-xl font-bold capitalize">{learningMetrics.weight_adjustments?.strategy_bias || "Neutral"}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Trades Analyzed</div>
                <div className="text-xl font-bold">{learningMetrics.trades_analyzed || 0}</div>
              </div>
            </div>
          </Card>
        )}

        {/* Trade History by Date */}
        {loading ? (
          <Card className="p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </Card>
        ) : tradesWithAllocation.length === 0 ? (
          <Card className="p-8 text-center">
            <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Trade History Yet</h3>
            <p className="text-muted-foreground mb-4">
              Trade history will appear here after the AI system evaluates trades at market close.
            </p>
            <p className="text-sm text-muted-foreground">
              The evaluation runs automatically at 3:45 PM IST each trading day.
            </p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--surface))]">
                  <tr>
                    {isColumnVisible('date') && <th className="text-left p-3 font-medium">Trade Date</th>}
                    {isColumnVisible('eval_datetime') && <th className="text-left p-3 font-medium">Evaluation Time</th>}
                    {isColumnVisible('symbol') && <th className="text-left p-3 font-medium">Stock Name</th>}
                    {isColumnVisible('sector') && <th className="text-left p-3 font-medium">Industry</th>}
                    {isColumnVisible('rank_score') && <th className="text-center p-3 font-medium">Rank Score</th>}
                    {isColumnVisible('strategy') && <th className="text-center p-3 font-medium">Trading Signal</th>}
                    {isColumnVisible('recommendation') && <th className="text-center p-3 font-medium">Recommendation</th>}
                    {isColumnVisible('shares') && <th className="text-right p-3 font-medium">Number of Shares</th>}
                    {isColumnVisible('investment') && <th className="text-right p-3 font-medium">Investment Amount</th>}
                    {isColumnVisible('start_price') && <th className="text-right p-3 font-medium">Entry Price</th>}
                    {isColumnVisible('predicted_price') && <th className="text-right p-3 font-medium">Target Price (2-Day)</th>}
                    {isColumnVisible('actual_price') && <th className="text-right p-3 font-medium">Actual Price</th>}
                    {isColumnVisible('gross_pl') && <th className="text-right p-3 font-medium">Gross Profit / Loss</th>}
                    {isColumnVisible('commission') && <th className="text-right p-3 font-medium">Brokerage Fee</th>}
                    {isColumnVisible('net_pl') && <th className="text-right p-3 font-medium">Net Profit / Loss</th>}
                    {isColumnVisible('eval_date') && <th className="text-left p-3 font-medium">Review Date</th>}
                    {isColumnVisible('result') && <th className="text-center p-3 font-medium">Trade Outcome</th>}
                    {isColumnVisible('success') && <th className="text-center p-3 font-medium">Prediction Success</th>}
                    {isColumnVisible('actions') && <th className="text-center p-3 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {tradesWithAllocation.map((trade, idx) => {
                    // Use calculated strategy
                    const strategy = trade.calculated_strategy;
                    const recommendation = 'TRADE';
                    
                    // Determine result status based on calculated values
                    const getResultStatus = () => {
                      const netProfit = trade.calculated_net_profit || 0;
                      const actualPrice = trade.actual_price || 0;
                      
                      // Use backend success field if available
                      if (trade.success) {
                        return trade.success === "Successful" ? 'success' : 'failed';
                      }
                      
                      // Fallback: No actual price yet - pending
                      if (!actualPrice) return 'pending';
                      
                      // Success = profitable, Failed = not profitable
                      if (netProfit > 0) return 'success';
                      return 'failed';
                    };
                    
                    const resultStatus = getResultStatus();
                    
                    // Render result icon based on status
                    const renderResultIcon = () => {
                      switch (resultStatus) {
                        case 'success':
                          return (
                            <div className="flex flex-col items-center">
                              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                              <span className="text-[10px] text-emerald-400 mt-0.5">Successful</span>
                            </div>
                          );
                        case 'failed':
                          return (
                            <div className="flex flex-col items-center">
                              <XCircle className="h-5 w-5 text-red-400" />
                              <span className="text-[10px] text-red-400 mt-0.5">Not Successful</span>
                            </div>
                          );
                        case 'pending':
                          return <span className="text-muted-foreground">Pending</span>;
                        default:
                          return <span className="text-muted-foreground">—</span>;
                      }
                    };
                    
                    return (
                      <tr 
                        key={`${trade.date || trade.trade_date}-${trade.symbol}-${idx}`}
                        className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface))]/50"
                      >
                        {isColumnVisible('date') && (
                          <td className="p-3 font-mono text-sm">{trade.date || trade.trade_date}</td>
                        )}
                        {isColumnVisible('eval_datetime') && (
                          <td className="p-3 font-mono text-sm">
                            {trade.evaluated_at ? (
                              <span>
                                {new Date(trade.evaluated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                {' '}
                                <span className="text-muted-foreground">
                                  {new Date(trade.evaluated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                </span>
                              </span>
                            ) : '—'}
                          </td>
                        )}
                        {isColumnVisible('symbol') && (
                          <td className="p-3 font-medium">{trade.symbol?.replace('.NS', '')}</td>
                        )}
                        {isColumnVisible('sector') && (
                          <td className="p-3 text-sm text-muted-foreground">{trade.sector || '—'}</td>
                        )}
                        {isColumnVisible('rank_score') && (
                          <td className="p-3 text-center">
                            <span className="text-[hsl(var(--primary))] font-semibold">
                              {trade.ranking_score ? trade.ranking_score.toFixed(1) : '—'}
                            </span>
                          </td>
                        )}
                        {isColumnVisible('strategy') && (
                          <td className="p-3 text-center">
                            <Badge className={STRATEGY_COLORS[strategy]}>{strategy}</Badge>
                          </td>
                        )}
                        {isColumnVisible('recommendation') && (
                          <td className="p-3 text-center">
                            <Badge className={RECOMMENDATION_COLORS[recommendation]}>{recommendation}</Badge>
                          </td>
                        )}
                        {isColumnVisible('shares') && (
                          <td className="p-3 text-right font-mono">{trade.calculated_shares > 0 ? trade.calculated_shares.toLocaleString() : '—'}</td>
                        )}
                        {isColumnVisible('investment') && (
                          <td className="p-3 text-right font-mono">{trade.calculated_investment > 0 ? formatCurrency(trade.calculated_investment, 0) : '—'}</td>
                        )}
                        {isColumnVisible('start_price') && (
                          <td className="p-3 text-right font-mono">{formatCurrency(trade.start_price)}</td>
                        )}
                        {isColumnVisible('predicted_price') && (
                          <td className="p-3 text-right font-mono text-[hsl(var(--primary))]">{formatCurrency(trade.predicted_price)}</td>
                        )}
                        {isColumnVisible('actual_price') && (
                          <td className="p-3 text-right font-mono">{trade.actual_price ? formatCurrency(trade.actual_price) : '—'}</td>
                        )}
                        {isColumnVisible('gross_pl') && (
                          <td className={`p-3 text-right font-mono ${trade.calculated_gross_profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {trade.actual_price ? formatCurrency(trade.calculated_gross_profit) : '—'}
                          </td>
                        )}
                        {isColumnVisible('commission') && (
                          <td className="p-3 text-right font-mono text-red-400">
                            {trade.actual_price ? `-${formatCurrency(trade.calculated_commission)}` : '—'}
                          </td>
                        )}
                        {isColumnVisible('net_pl') && (
                          <td className={`p-3 text-right font-mono ${trade.calculated_net_profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {trade.actual_price ? formatCurrency(trade.calculated_net_profit) : '—'}
                          </td>
                        )}
                        {isColumnVisible('eval_date') && (
                          <td className="p-3 font-mono text-sm">
                            {trade.evaluation_date || '—'}
                          </td>
                        )}
                        {isColumnVisible('result') && (
                          <td className="p-3 text-center">
                            {renderResultIcon()}
                          </td>
                        )}
                        {isColumnVisible('success') && (
                          <td className="p-3 text-center">
                            {trade.success ? (
                              <Badge className={trade.success === "Successful" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                                {trade.success}
                              </Badge>
                            ) : trade.actual_price ? (
                              trade.calculated_net_profit > 0 ? (
                                <Badge className="bg-green-500/20 text-green-400">Successful</Badge>
                              ) : (
                                <Badge className="bg-red-500/20 text-red-400">Not Successful</Badge>
                              )
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        )}
                        {isColumnVisible('actions') && (
                          <td className="p-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReEvalDialog(trade)}
                              disabled={reEvaluatingStock === trade.symbol}
                              className="h-7 px-2 text-xs"
                              data-testid={`re-evaluate-btn-${trade.symbol}`}
                            >
                              {reEvaluatingStock === trade.symbol ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  Re-Eval
                                </>
                              )}
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Link to Legacy History */}
        <div className="text-center">
          <Link to="/history">
            <Button variant="ghost" className="text-muted-foreground hover:text-foreground gap-2">
              <History className="h-4 w-4" />
              View Legacy Prediction History
            </Button>
          </Link>
        </div>
      </div>

      {/* Re-Evaluate Dialog */}
      <Dialog open={showReEvalDialog} onOpenChange={setShowReEvalDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Re-Evaluate Stock</DialogTitle>
            <DialogDescription>
              Re-evaluate {reEvalSymbol?.replace('.NS', '')} with historical closing price.
              Leave date blank to use the original review date.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="eval-date">Evaluation Date (optional)</Label>
              <Input
                id="eval-date"
                type="date"
                value={reEvalDate}
                onChange={(e) => setReEvalDate(e.target.value)}
                placeholder="YYYY-MM-DD"
                className="mt-1"
                data-testid="re-eval-date-input"
              />
              {reEvalTradeEvalDate && (
                <p className="text-xs text-muted-foreground mt-2">
                  Original review date: <strong>{reEvalTradeEvalDate}</strong>
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                If blank, will use the stock's review date to fetch closing price.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReEvalDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={reEvaluateStock}
              disabled={reEvaluatingStock}
              data-testid="confirm-re-eval-btn"
              className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
            >
              {reEvaluatingStock ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Evaluating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Re-Evaluate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Evaluation Dialog */}
      <Dialog open={showForceEvalDialog} onOpenChange={setShowForceEvalDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Force Evaluation - All Stocks</DialogTitle>
            <DialogDescription>
              Re-evaluate ALL stocks in history using historical END OF DAY closing prices.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="force-eval-date">Evaluation Date (optional)</Label>
              <Input
                id="force-eval-date"
                type="date"
                value={forceEvalDate}
                onChange={(e) => setForceEvalDate(e.target.value)}
                placeholder="YYYY-MM-DD"
                className="mt-1"
                data-testid="force-eval-date-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                <strong>If blank:</strong> Each stock will use its own Review Date to fetch the closing price.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                <strong>If date provided:</strong> All stocks will use this date for evaluation.
              </p>
            </div>
            <div className="bg-[hsl(var(--surface))] p-3 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Total stocks in history:</strong> {tradeHistory.length}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForceEvalDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={runForceEvaluation}
              disabled={evaluating || tradeHistory.length === 0}
              data-testid="confirm-force-eval-btn"
              className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
            >
              {evaluating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Evaluating {tradeHistory.length} stocks...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Evaluate All Stocks
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
